import type { Goal, Quantity } from "@medplum/fhirtypes";

const LOINC_SYSTEM = "http://loinc.org";
const UCUM_SYSTEM = "http://unitsofmeasure.org";

export const TREND_GOAL_IDENTIFIER_SYSTEM = "vibrant.trends.biomarker-goal";

/** Identifier system used to tag AI-generated goals (vs manually created ones) */
export const TREND_GOAL_SOURCE_SYSTEM = "vibrant.trends.goal-source";

/** Identifier value for AI-generated goals */
export const AI_GENERATED_GOAL_SOURCE = "ai-generated";

/** Extension URL for 1-based priority when goal was part of a "top N" ranked set */
export const TREND_GOAL_PRIORITY_EXTENSION_URL = "vibrant.trends.goal-priority";

export interface TrendGoalResponse {
  id: string;
  loincCode: string;
  min: number;
  max: number;
  setBy: string;
  setAt: string;
  /** AI reasoning for the goal range (from Goal.note), if available */
  reasoning?: string;
  /** Whether this goal was AI-generated */
  isAIGenerated?: boolean;
  /** 1-based rank when goal was generated as part of a "top N" set (from extension) */
  priority?: number;
}

interface TrendGoalTarget {
  loincCode: string;
  displayName?: string;
  min: number;
  max: number;
  unit?: string;
}

/**
 * Builds the identifier used to tag trend goals.
 */
export function buildTrendGoalIdentifier({
  patientId,
  loincCode,
  practiceId,
}: {
  patientId: string;
  loincCode: string;
  practiceId?: string;
}): { system: string; value: string } {
  return {
    system: TREND_GOAL_IDENTIFIER_SYSTEM,
    value: practiceId ? `${patientId}:${practiceId}:${loincCode}` : `${patientId}:${loincCode}`,
  };
}

export interface BuildExpressedByInput {
  profile?: { resourceType?: string; id?: string } | null;
}

/**
 * Builds the expressedBy reference for the current profile, when available.
 */
export function buildExpressedBy({
  profile,
}: BuildExpressedByInput): Goal["expressedBy"] | undefined {
  if (!profile?.resourceType || !profile?.id) return undefined;

  return {
    reference: `${profile.resourceType}/${profile.id}`,
  };
}

/**
 * Builds both identifiers for an AI-generated trend goal:
 * 1. The standard biomarker identifier (for dedup/lookup)
 * 2. The AI source identifier (for programmatic filtering)
 */
export function buildAIGoalIdentifiers({
  patientId,
  loincCode,
  practiceId,
}: {
  patientId: string;
  loincCode: string;
  practiceId?: string;
}): Goal["identifier"] {
  return [
    buildTrendGoalIdentifier({ patientId, loincCode, practiceId }),
    { system: TREND_GOAL_SOURCE_SYSTEM, value: AI_GENERATED_GOAL_SOURCE },
  ];
}

/**
 * Returns true when the Goal is a trend goal created by this feature.
 */
export function isTrendGoal({ goal }: { goal: Goal }): boolean {
  return Boolean(
    goal.identifier?.some((identifier) => identifier.system === TREND_GOAL_IDENTIFIER_SYSTEM),
  );
}

/**
 * Returns true when the Goal was generated by AI.
 */
export function isAIGeneratedGoal({ goal }: { goal: Goal }): boolean {
  return Boolean(
    goal.identifier?.some(
      (identifier) =>
        identifier.system === TREND_GOAL_SOURCE_SYSTEM &&
        identifier.value === AI_GENERATED_GOAL_SOURCE,
    ),
  );
}

/**
 * Removes the AI-generated source marker when a human/service update takes
 * ownership of an existing AI goal.
 */
export function removeAIGeneratedGoalSource(
  identifiers: NonNullable<Goal["identifier"]>,
): NonNullable<Goal["identifier"]> {
  return identifiers.filter(
    (identifier) =>
      !(
        identifier.system === TREND_GOAL_SOURCE_SYSTEM &&
        identifier.value === AI_GENERATED_GOAL_SOURCE
      ),
  );
}

/**
 * Builds a Goal.target payload for a biomarker range.
 */
export function buildTrendGoalTarget({
  loincCode,
  displayName,
  unit,
  min,
  max,
}: {
  loincCode: string;
  displayName?: string;
  unit?: string;
  min: number;
  max: number;
}): Goal["target"] {
  return [
    {
      measure: {
        coding: [
          {
            system: LOINC_SYSTEM,
            code: loincCode,
            display: displayName,
          },
        ],
        text: displayName,
      },
      detailRange: {
        low: buildQuantity({ value: min, unit }),
        high: buildQuantity({ value: max, unit }),
      },
    },
  ];
}

/**
 * Parses a trend goal response shape from a Goal resource.
 */
export function toTrendGoalResponse({ goal }: { goal: Goal }): TrendGoalResponse | undefined {
  if (!goal.id || !isTrendGoal({ goal })) return undefined;

  const target = getTrendGoalTarget({ goal });
  if (!target) return undefined;

  const setBy = goal.expressedBy?.display ?? goal.expressedBy?.reference ?? "unknown";
  const setAt = goal.statusDate ?? goal.startDate ?? goal.meta?.lastUpdated;
  if (!setAt) return undefined;

  const aiGenerated = isAIGeneratedGoal({ goal });
  const reasoning = goal.note?.[0]?.text;
  const priority = getTrendGoalPriority({ goal });

  return {
    id: goal.id,
    loincCode: target.loincCode,
    min: target.min,
    max: target.max,
    setBy,
    setAt,
    ...(reasoning ? { reasoning } : {}),
    ...(aiGenerated ? { isAIGenerated: true } : {}),
    ...(priority != null ? { priority } : {}),
  };
}

/**
 * Extracts 1-based priority from a trend goal's extension (when set by "top N" generation).
 */
export function getTrendGoalPriority({ goal }: { goal: Goal }): number | undefined {
  const ext = goal.extension?.find((e) => e.url === TREND_GOAL_PRIORITY_EXTENSION_URL);
  const value = ext?.valueInteger;
  return typeof value === "number" && Number.isInteger(value) && value >= 1 ? value : undefined;
}

/**
 * Extracts the biomarker target range from a Goal resource.
 */
export function getTrendGoalTarget({ goal }: { goal: Goal }): TrendGoalTarget | undefined {
  const target = goal.target?.find((candidate) =>
    candidate.measure?.coding?.some((coding) => Boolean(coding.code)),
  );
  if (!target) return undefined;

  const loincCoding =
    target.measure?.coding?.find((coding) => coding.system === LOINC_SYSTEM && coding.code) ??
    target.measure?.coding?.find((coding) => coding.code);

  const minValue = target.detailRange?.low?.value;
  const maxValue = target.detailRange?.high?.value;

  if (!loincCoding?.code || !isFiniteNumber(minValue) || !isFiniteNumber(maxValue)) {
    return undefined;
  }

  // Reject malformed goals with inverted ranges
  if (minValue > maxValue) {
    return undefined;
  }

  return {
    loincCode: loincCoding.code,
    displayName: loincCoding.display ?? target.measure?.text,
    min: minValue,
    max: maxValue,
    unit: target.detailRange?.low?.unit ?? target.detailRange?.high?.unit,
  };
}

/**
 * Returns true when a value is a finite number.
 */
function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * Builds a Quantity payload for Goal target ranges.
 */
function buildQuantity({ value, unit }: { value: number; unit?: string }): Quantity {
  if (!unit) {
    return { value };
  }

  return {
    value,
    unit,
    system: UCUM_SYSTEM,
    code: unit,
  };
}
